import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  setPersonTagOverrides,
  subscribeTagOverrides,
  listPersonTagOverrides,
  type PersonTagOverride,
} from '@/utils/artifact-tagging';

let loadedOnce = false;

const fetchAll = async (): Promise<PersonTagOverride[]> => {
  const { data, error } = await supabase
    .rpc('public_person_tag_overrides');
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    personSlug: row.person_slug,
    displayName: row.display_name ?? null,
    tags: row.tags ?? [],
    category: row.category ?? null,
    keywords: row.keywords ?? [],
    subjects: row.subjects ?? [],
    shortBio: row.short_bio ?? null,
    curatorNotes: row.curator_notes ?? null,
  }));
};

export const usePersonTagOverrides = () => {
  const [, setTick] = useState(0);
  const [loading, setLoading] = useState(!loadedOnce);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAll();
      setPersonTagOverrides(rows);
      loadedOnce = true;
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = subscribeTagOverrides(() => setTick((n) => n + 1));
    if (!loadedOnce) void refresh();
    return () => {
      unsub();
    };
  }, [refresh]);

  return { overrides: listPersonTagOverrides(), loading, error, refresh };
};